"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Drawer, DrawerContent } from "./ui/drawer";
import { useSpecialAccessContext } from "@/context/SpecialAccessProvider";
import { Img } from "./Img";
import { Text } from "./Text";
import { useRouter } from "next/navigation";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  URLQueryParamsEnum,
  EventNamesEnum,
  EventPropertiesEnum,
  SpecialAccessWidgetTypeEnum,
  SpecialAccessSourceEnum,
} from "@/constants/constants";
import { getClientSideCookieValue } from "@/utils/common.helpers";
import FullScreenLoader from "./FullScreenLoader";
import { VideoPlayer } from "./VideoPlayer";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { Column } from "./Stack";
import { ConsumptionStateCategoryEnum } from "@/screens/specialAccess/types/specialAccess.types";

export const SpecialAccessDrawer = ({
  showSpecialAccessBanner,
}: {
  showSpecialAccessBanner: boolean;
}) => {
  const { currentSpecialAccessData, totalConsumptionStates } =
    useSpecialAccessContext();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useTrackingAnalytics();

  // Get hook_clip from current state
  const hookClip = currentSpecialAccessData?.hook_clip;

  // Get contentSlug from currentSpecialAccessData
  const contentSlug = currentSpecialAccessData?.content?.slug;

  // Show drawer only when banner is shown and hook clip exists
  const shouldShowDrawer = useMemo(
    () => showSpecialAccessBanner && !!hookClip?.asset_url,
    [showSpecialAccessBanner, hookClip?.asset_url],
  );

  // Get watch page URL from cookie
  const watchPageUrl = getClientSideCookieValue(
    cookiesValues[CookiesEnum.SpecialAccessWatchPageUrl],
  );

  // Generate checkout URL with watch page redirect
  const checkoutUrl = useMemo(() => {
    if (watchPageUrl) {
      return `${AppRoutesEnum.CHECKOUT}?${URLQueryParamsEnum.REDIRECTION_AFTER_PAYMENT_TO}=${watchPageUrl}`;
    }
    return null;
  }, [watchPageUrl]);

  // Prefetch checkout URL on mount
  useEffect(() => {
    if (checkoutUrl) {
      router.prefetch(checkoutUrl);
    }
  }, [checkoutUrl, router]);

  // Control drawer open/close state
  useEffect(() => {
    setIsOpen(shouldShowDrawer);
  }, [shouldShowDrawer]);

  // Reset loading state when drawer opens (e.g., when coming back from watch page)
  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  // Track SPECIAL_ACCESS_WIDGET_VIEW event when drawer opens
  useEffect(() => {
    // Track when drawer should be shown and is actually open, and we have the slug
    if (isOpen && contentSlug && hookClip?.cta?.text) {
      trackEvent({
        eventName: EventNamesEnum.SPECIAL_ACCESS_WIDGET_VIEW,
        properties: {
          [EventPropertiesEnum.CONTENT_SLUG]: contentSlug,
          [EventPropertiesEnum.WIDGET_TYPE]:
            SpecialAccessWidgetTypeEnum.COMING_UP_DRAWER,
          [EventPropertiesEnum.WIDGET_SOURCE]:
            SpecialAccessSourceEnum.SPECIAL_ACCESS_COMING_UP_DRAWER,
          [EventPropertiesEnum.CTA_NAME]: hookClip?.cta?.text,
        },
      });
    }
  }, [isOpen, contentSlug, hookClip?.cta?.text]);

  const handleCTA = useCallback(() => {
    // Track SPECIAL_ACCESS_WIDGET_CLICKED event
    if (contentSlug) {
      trackEvent({
        eventName: EventNamesEnum.SPECIAL_ACCESS_WIDGET_CLICKED,
        properties: {
          [EventPropertiesEnum.CONTENT_SLUG]: contentSlug,
          [EventPropertiesEnum.WIDGET_TYPE]:
            SpecialAccessWidgetTypeEnum.COMING_UP_DRAWER,
          [EventPropertiesEnum.WIDGET_SOURCE]:
            SpecialAccessSourceEnum.SPECIAL_ACCESS_COMING_UP_DRAWER,
          [EventPropertiesEnum.CTA_NAME]: hookClip?.cta?.text,
        },
      });
    }

    setIsLoading(true);

    if (
      currentSpecialAccessData &&
      totalConsumptionStates !== null &&
      watchPageUrl
    ) {
      const currentStateValue =
        currentSpecialAccessData.current_state_value + 1;

      if (
        currentSpecialAccessData.current_state_category ===
          ConsumptionStateCategoryEnum.Consumption &&
        currentStateValue < totalConsumptionStates
      ) {
        // Link to watch page
        router.push(decodeURIComponent(watchPageUrl));
      } else if (checkoutUrl) {
        // Link to checkout with redirectionAfterPaymentTo
        router.push(checkoutUrl);
      } else {
        console.error("No checkout URL found");
        setIsLoading(false);
      }
    } else if (watchPageUrl) {
      router.push(decodeURIComponent(watchPageUrl));
    } else {
      console.error("No checkout URL or watch page URL found");
      setIsLoading(false);
    }
  }, [
    checkoutUrl,
    router,
    watchPageUrl,
    currentSpecialAccessData,
    totalConsumptionStates,
    contentSlug,
    trackEvent,
  ]);

  // Early return if drawer shouldn't be shown
  if (!shouldShowDrawer || !hookClip) {
    return null;
  }

  return (
    <>
      {/* Fullscreen loader */}
      {isLoading && <FullScreenLoader isBlur={true} />}
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent
          data-testid="special-access-drawer"
          className="bg-special-access-drawer-gradient pb-12 pt-2 backdrop-blur-sm"
        >
          <div className="relative flex w-full flex-col gap-8 px-2 pt-6">
            {/* "Special Preview Ended" banner */}
            {hookClip.tag_url && (
              <div
                data-testid="special-access-drawer-tag"
                className="absolute left-1/2 top-4 z-10 flex w-full -translate-x-1/2 items-center justify-center"
              >
                <Img
                  src={hookClip.tag_url}
                  alt={hookClip.tag_text || "Special Preview Ended"}
                  width={180}
                  height={24}
                />
              </div>
            )}

            <div className="flex flex-col rounded-2xl bg-stg-grey-1">
              {/* Video player and "Coming up" tag */}
              <div className="bg-newPrimary relative aspect-video w-full overflow-hidden rounded-t-2xl">
                {/* Video player */}
                <VideoPlayer
                  data-testid="special-access-drawer-video"
                  videoSrc={hookClip.asset_url}
                  autoPlay={true}
                  showControls={false}
                  loop={true}
                  className="h-full w-full object-cover"
                />

                {/* "Coming up" tag */}
                {hookClip.tag_text && (
                  <div
                    data-testid="special-access-drawer-coming-up-tag"
                    className="absolute bottom-3 left-3"
                  >
                    <div className="rounded bg-stg-white px-2 py-1 opacity-50 backdrop-blur-sm">
                      <Text size={1} appearance="black" weight="medium">
                        {hookClip.tag_text}
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              {/* Title text */}
              {hookClip.title?.text && (
                <div
                  className="flex h-14 items-center justify-center text-wrap"
                  style={{
                    color: `#${hookClip.title.color}`,
                  }}
                >
                  <Column justify="center" align="center" gap={0.5}>
                    <Text
                      size={4}
                      weight="bold"
                      dangerouslyDisableDefaultTextColor
                    >
                      {hookClip.title.text}
                    </Text>
                    {hookClip.description?.text && (
                      <Text size={2} weight="medium">
                        {hookClip.description.text}
                      </Text>
                    )}
                  </Column>
                </div>
              )}
            </div>

            {/* CTA Button */}
            {hookClip.cta && (
              <button
                data-testid="special-access-drawer-cta-button"
                onClick={handleCTA}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-lg py-4 transition-opacity active:opacity-80"
                style={{
                  backgroundColor: `#${hookClip.cta.bg_color}`,
                  color: `#${hookClip.cta.text_color}`,
                }}
                aria-label={hookClip.cta.text}
              >
                {hookClip.cta.prefix_icon && (
                  <div
                    style={{
                      maskImage: `url(${hookClip.cta.prefix_icon})`,
                      maskSize: "contain",
                      maskRepeat: "no-repeat",
                      maskPosition: "center",
                      WebkitMaskImage: `url(${hookClip.cta.prefix_icon})`,
                      WebkitMaskSize: "contain",
                      WebkitMaskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                      backgroundColor: `#${hookClip.cta.text_color}`,
                      width: "1rem",
                      height: "1rem",
                    }}
                  />
                )}
                <Text size={3} weight="bold" dangerouslyDisableDefaultTextColor>
                  {hookClip.cta.text}
                </Text>
              </button>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
